import Cookies from "js-cookie";
import { useEffect, useRef } from "react";
import { printUniversal } from "../pages/pos/universalPrint";
import { ReceiptOrder } from "../pages/pos/receiptPrint";

const getRestaurantId = (): string | null => {
  try {
    // Admin user stored as JSON in "user" cookie
    const raw = Cookies.get("user");
    if (raw) {
      const user = JSON.parse(raw);
      if (user?.restaurantId) return String(user.restaurantId);
    }
  } catch { /* ignore */ }

  try {
    // Employee user — restaurantId is inside the emptoken JWT payload
    const emptoken = Cookies.get("emptoken");
    if (emptoken) {
      const payload = JSON.parse(atob(emptoken.split(".")[1]));
      if (payload?.restaurantId) return String(payload.restaurantId);
    }
  } catch { /* ignore */ }

  return null;
};

const BACKEND_WS_URL = process.env.REACT_APP_BACKEND_WS_URL;
const SECRET = process.env.REACT_APP_PRINT_SERVICE_SECRET;

/**
 * Maintains a persistent WebSocket connection to the backend.
 * When a print job arrives, it prints automatically via QZ Tray.
 * Auto-reconnects with exponential backoff if the connection drops.
 *
 * Mount this once at the App level — it runs for the entire session.
 */
const usePrintWebSocket = (currencySymbol = "$") => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelay = useRef(2000);
  const mounted = useRef(true);

  const connect = () => {
    if (!BACKEND_WS_URL || !mounted.current) return;

    const url = SECRET
      ? `${BACKEND_WS_URL}?secret=${encodeURIComponent(SECRET)}`
      : BACKEND_WS_URL;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectDelay.current = 2000;
      console.log("[PrintWS] Connected to backend — auto-print active");
      const restaurantId = getRestaurantId();
      console.log(`[PrintWS] Detected restaurant ID: ${restaurantId}`);
      if (restaurantId) {
        ws.send(JSON.stringify({ type: "register", restaurantId }));
        console.log(`[PrintWS] Registered for restaurant ${restaurantId}`);
      }
    };

    ws.onmessage = async (e: MessageEvent) => {
      try {
        const msg = JSON.parse(e.data as string);
        if (msg.type !== "print") return;

        const { order, logoBase64 } = msg as {
          order: ReceiptOrder;
          logoBase64?: string;
        };

        if (!order?._id || !order?.orderNumber) return;

        console.log(`[PrintWS] 🖨 Printing order #${order.orderNumber}`);
        try {
          await printUniversal(order, logoBase64, undefined, currencySymbol);
          ws.send(JSON.stringify({ type: "ack", orderId: order._id }));
          console.log(`[PrintWS] ✅ Order #${order.orderNumber} printed`);
        } catch (printErr) {
          ws.send(JSON.stringify({ type: "nack", orderId: order._id }));
          console.error("[PrintWS] Print error:", printErr);
        }
      } catch (err) {
        console.error("[PrintWS] Message parse error:", err);
      }
    };

    ws.onclose = () => {
      if (!mounted.current) return;
      console.warn(
        `[PrintWS] Disconnected. Reconnecting in ${reconnectDelay.current / 1000}s...`,
      );
      reconnectTimer.current = setTimeout(() => {
        reconnectDelay.current = Math.min(
          reconnectDelay.current * 1.5,
          30_000,
        );
        connect();
      }, reconnectDelay.current);
    };

    ws.onerror = () => {
      // onclose fires after onerror — reconnect is handled there
    };
  };

  useEffect(() => {
    mounted.current = true;
    connect();

    return () => {
      mounted.current = false;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default usePrintWebSocket;
